/**
 * Aviso no login — "Geração automática agora é dos planos pagos" (F3 do
 * assistente de configuração, out/2026).
 *
 * Desde a proposta de correção da configuração inicial, a conexão com o
 * inversor é dos planos pagos. A conta Free que JÁ tinha inversor conectado
 * perde a coleta automática (o backend ignora a conta nos crons; a conexão e o
 * histórico ficam guardados e voltam sozinhos se o cliente migrar). Este modal
 * explica a mudança UMA vez por usuário — a marca fica no backend
 * (`users.monitoring_downgrade_notice_seen_at`), não no navegador. Sem data no
 * texto (decisão do Bruno: não informar a data de corte).
 */
import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Modal } from 'ui';
import { Button } from 'ui/button/button.ui';
import { alert } from 'lib/alert';

import { useAppSelector } from 'store/hooks';
import * as selectUser from 'store/user/user.selector';
import { selectConsumerUnitList } from 'store/consumer_unit/consumer_unit.selector';

import { isAccountOwner } from 'entities/User';
import {
  markMonitoringDowngradeNoticeSeenService,
  requestPlanContractService,
} from 'services/user.service';
import { canIntegrateMonitoring, getPlanRuleInfo } from 'utils/planRules';
import { format } from 'utils/format';
import { INVERTER_APPS } from 'utils/inverterApps';

import {
  Lead,
  Notice,
  StepTag,
  WizardBody,
  WizardFooter,
} from 'components/setup-wizard/setup-wizard.styles';

export const MonitoringDowngradeNotice = () => {
  const navigate = useNavigate();
  const currentUser = useAppSelector(selectUser.currentUser);
  const isImpersonating = useAppSelector(selectUser.isImpersonating);
  const consumerUnitList = useAppSelector(selectConsumerUnitList);

  const [closed, setClosed] = useState(false);
  const [sending, setSending] = useState(false);

  // Usina ligada a um monitoramento (conexão própria ou conta mestre)
  const connectedPlant = useMemo(() => {
    for (const uc of consumerUnitList) {
      const plant = (uc.plantList ?? []).find(
        (p) => !!p.monitoringSystemUser?.id && !!p.identifier
      );
      if (plant) return plant;
    }
    return undefined;
  }, [consumerUnitList]);

  const show =
    !closed &&
    !isImpersonating &&
    !!currentUser &&
    !canIntegrateMonitoring(currentUser.plan?.name ?? 'Free') &&
    !currentUser.monitoringDowngradeNoticeSeenAt &&
    !!connectedPlant;

  if (!show) return null;

  const slug =
    connectedPlant?.monitoringSystemUser?.monitoringSystem?.slug ?? '';
  const brand =
    connectedPlant?.monitoringSystemUser?.monitoringSystem?.name ??
    'do seu inversor';
  const app = INVERTER_APPS[slug]?.app ?? 'do seu inversor';
  const starterPrice = getPlanRuleInfo('Starter')?.price ?? 0;
  const owner = isAccountOwner(currentUser);

  const close = () => {
    setClosed(true);
    markMonitoringDowngradeNoticeSeenService().catch(() => {
      /* sem a marca, o aviso volta no próximo login — aceitável */
    });
  };

  const hire = async () => {
    setSending(true);
    try {
      await requestPlanContractService('Starter');
      alert.success(
        'Pedido enviado! Nossa equipe vai entrar em contato pelo seu e-mail.'
      );
      close();
    } catch (err) {
      alert.error(
        (err as { message?: string })?.message ||
          'Não foi possível enviar o pedido.'
      );
    } finally {
      setSending(false);
    }
  };

  return (
    <Modal show close={close} title="Mudança no plano Free">
      <WizardBody>
        <StepTag>Mudança no plano Free</StepTag>
        <h3>Geração automática agora é dos planos pagos</h3>
        <Lead>
          A geração do seu inversor {brand} não é mais buscada automaticamente
          no plano Free. O histórico já coletado continua salvo.
        </Lead>
        <Lead>
          No Free, você continua informando a geração ao importar cada conta,
          consultando o app {app}. Os alertas de inversor parado também deixam
          de ser enviados.
        </Lead>
        <Notice tone="info">
          <p>
            <b>Starter · R$ {format.money(starterPrice)}/mês</b> — geração
            automática do inversor, alerta de sistema parado e fatura recebida
            por e-mail. A conexão volta sozinha, sem configurar de novo.
          </p>
        </Notice>
        {owner && (
          <Lead>
            &ldquo;Quero contratar&rdquo; envia o pedido à nossa equipe, como na
            página Conta. Este aviso aparece uma vez.
          </Lead>
        )}
      </WizardBody>
      <WizardFooter>
        <Button variant="inverse" type="button" onClick={close}>
          Continuar no Free
        </Button>
        <span style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <Button
            variant="secondary"
            type="button"
            onClick={() => {
              close();
              navigate('/conta');
            }}
          >
            Comparar planos
          </Button>
          {owner && (
            <Button type="button" onClick={hire} disabled={sending}>
              {sending ? 'Enviando…' : 'Quero contratar o Starter'}
            </Button>
          )}
        </span>
      </WizardFooter>
    </Modal>
  );
};
